import { PERK_REDEMPTION_STATUS } from "~constants/status"
import { ModalTitle } from "~styled/modal"
import Modal from "~app/Modal"
import { useState } from "react"
import { handleRedemptionStatusChange } from "~components/CCLApprovalModal"
import { WizardRow, WizardColumn } from "~styled/wizard"
import { useForm, FormProvider } from "react-hook-form"
import { ERROR_MESSAGES } from "~constants/errorMessages"
import ControlSelect from "~app/ControlSelect"
import Textarea from "~app/Textarea"
import ConfirmationModal from "~components/ConfirmationModal"

interface Props {
  isOpen: boolean
  setIsOpen: (v: boolean) => void
  selectedRedemptionId: string
  refreshList: () => void
  bookingNumber?: string
}

export enum REJECTION_REASONS {
  BOOKING_CANCELLED = "BOOKING_CANCELLED",
  BOOKING_SAILED = "BOOKING_SAILED",
  OBC_ALREADY_APPLIED = "OBC_ALREADY_APPLIED",
  INVALID_FARE = "INVALID_FARE",
  INVALID_BOOKING = "INVALID_BOOKING",
  NAME_MATCH_ERROR_BOOKING = "NAME_MATCH_ERROR_BOOKING",
  INCORRECT_CRUISE_LINE = "INCORRECT_CRUISE_LINE",
  OTHER = "OTHER",
}

const CCLRejectionModal = ({
  isOpen,
  setIsOpen,
  selectedRedemptionId,
  refreshList,
  bookingNumber,
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const methods = useForm()
  const {
    formState: { errors },
    handleSubmit,
    control,
    getValues,
    register,
  } = methods

  const rejectionReasonsList = [
    {
      value: REJECTION_REASONS.BOOKING_CANCELLED,
      label: "FAIL: Booking in canceled status",
    },
    {
      value: REJECTION_REASONS.BOOKING_SAILED,
      label: "FAIL: Sailing has already departed",
    },
    {
      value: REJECTION_REASONS.OBC_ALREADY_APPLIED,
      label: "FAIL: An OBC already exists for this reservation",
    },
    {
      value: REJECTION_REASONS.INVALID_FARE,
      label: "FAIL: Restricted discounted reservation code ineligible for OBC",
    },
    {
      value: REJECTION_REASONS.INVALID_BOOKING,
      label: "RESUBMIT: Cabin Book Num cannot be found",
    },
    {
      value: REJECTION_REASONS.NAME_MATCH_ERROR_BOOKING,
      label: "RESUBMIT: Guest's name does not match name on reservation",
    },
    {
      value: REJECTION_REASONS.INCORRECT_CRUISE_LINE,
      label: "RESUBMIT: Incorrect cruise line",
    },
    {
      value: REJECTION_REASONS.OTHER,
      label:
        "OTHER: for reasons not captured by the above. In the text below, please indicate if guest needs to resubmit",
    },
  ]

  const onSubmit = () => {
    setShowConfirmation(true)
  }

  const handleConfirm = () => {
    const values = getValues()
    handleRedemptionStatusChange(
      selectedRedemptionId,
      PERK_REDEMPTION_STATUS.INVALIDATED,
      values.cclRejectionReason?.value,
      values.cclRejectionDetails,
      null,
      null,
      () => setIsLoading(true),
      () => {
        refreshList()
        setIsOpen(false)
      },
      () => setIsLoading(false),
    )
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        buttonLabel={"Next"}
        isButtonDisabled={isLoading}
        isButtonLoading={isLoading}
        cancelButton={true}
        onRequestClose={() => setIsOpen(false)}
        onRequestConfirm={handleSubmit(onSubmit)}
      >
        <FormProvider {...methods}>
          <ModalTitle>OBC Request Rejected</ModalTitle>
          {bookingNumber && (
            <p>
              <strong>Booking Number:</strong> {bookingNumber}
            </p>
          )}
          <WizardRow>
            <WizardColumn>
              <ControlSelect
                name={"cclRejectionReason"}
                label={"Rejection Reason"}
                rules={{ required: true }}
                control={control}
                options={rejectionReasonsList}
                disabled={false}
                searchable={false}
                errorMessage={
                  errors.cclRejectionReason && ERROR_MESSAGES.FIELD_REQUIRED
                }
              />
            </WizardColumn>
            <WizardColumn>
              <Textarea
                name={"cclRejectionDetails"}
                label={"Rejection Details"}
                register={register("cclRejectionDetails")}
                errorMessage={errors.cclRejectionDetails}
              />
            </WizardColumn>
          </WizardRow>
          <WizardRow>
            <p>
              When you hit confirm, the guest will be sent an email confirming
              that their OBC request has been rejected.
            </p>
          </WizardRow>
        </FormProvider>
      </Modal>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirm}
        message="This action cannot be reverted, are you sure you want to Reject this OBC?"
        bookingNumber={bookingNumber}
      />
    </>
  )
}

export default CCLRejectionModal
